import { ArrowsClockwise, Package, ShoppingCart, TrendUp } from "@phosphor-icons/react"

import HoverCardRow from "~/components/reusable/hovercard/HoverCardRow"
import HoverCardRowWrapper from "~/components/reusable/hovercard/HoverCardRowWrapper"
import {
  MetricBreakdownFormula,
  MetricBreakdownHoverCard,
  MetricBreakdownNullState,
} from "~/components/reusable/hovercard/MetricBreakdownHoverCard"
import {
  getInventoryTurnPeriod,
  partialPeriodNote,
} from "~/components/reusable/inventoryTurnPeriod"
import { suffixedCurrencyFormatters } from "~/utils/formatting"
import { roundNumber } from "~/utils/helpers"

type Props = {
  revenue: number
  cogs: number
  avgInvValue: number
  /** Aggregated cost price for the row. <= 0 / null means no cost price is set on the underlying SKUs. */
  costPrice?: number | null
  currency?: string
  startDate?: Date | string | null
  children: React.ReactNode
}

export function GMROIBreakdownHoverCard({
  revenue,
  cogs,
  avgInvValue,
  costPrice,
  currency,
  startDate,
  children,
}: Props) {
  const grossProfit = revenue - cogs
  const gmroi = avgInvValue > 0 ? grossProfit / avgInvValue : null
  const missing: string[] = []
  if (costPrice == null || costPrice <= 0) missing.push("cost price")
  if (revenue <= 0) missing.push("revenue")
  if (cogs <= 0) missing.push("COGS")
  if (avgInvValue <= 0) missing.push("inventory history with stock")

  const period = getInventoryTurnPeriod(startDate)
  const { compact, full } = suffixedCurrencyFormatters(currency)

  return (
    <MetricBreakdownHoverCard
      title="GMROI breakdown"
      trigger={children}
      description={partialPeriodNote(period)}
    >
      {gmroi == null ? (
        <MetricBreakdownNullState
          label="GMROI"
          missing={missing}
          recorded={
            revenue > 0 && avgInvValue <= 0
              ? { label: "Revenue", value: compact(revenue) }
              : undefined
          }
        />
      ) : (
        <>
          <HoverCardRowWrapper>
            <HoverCardRow
              icon={<TrendUp size={14} />}
              label={`Revenue (${period.periodLabel})`}
              value={compact(revenue)}
            />
            <HoverCardRow
              icon={<ShoppingCart size={14} />}
              label={`COGS (${period.periodLabel})`}
              value={compact(cogs)}
            />
            <HoverCardRow
              icon={<Package size={14} />}
              label="Gross profit"
              value={compact(grossProfit)}
            />
          </HoverCardRowWrapper>
          <HoverCardRowWrapper>
            <HoverCardRow
              icon={<Package size={14} />}
              label={`Avg. monthly inventory (${period.periodLabel})`}
              value={compact(avgInvValue)}
            />
          </HoverCardRowWrapper>
          <HoverCardRowWrapper>
            <HoverCardRow
              icon={<ArrowsClockwise size={14} />}
              label="GMROI"
              value={roundNumber(gmroi, 2).toString()}
            />
          </HoverCardRowWrapper>
          <MetricBreakdownFormula>
            {`(${full(revenue)} - ${full(cogs)}) / ${full(avgInvValue)} = ${roundNumber(gmroi, 2)}`}
          </MetricBreakdownFormula>
        </>
      )}
    </MetricBreakdownHoverCard>
  )
}
